import React from 'react';
import {
  Alert,
  Card,
  Collapse,
  Row,
  Col,
  Statistic,
  Tag,
  Typography,
} from 'antd';
import {
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
  RocketOutlined,
} from '@ant-design/icons';

const { Text } = Typography;

/**
 * Parse an item that could be either a string or an object with name/message
 * String format: "Creative Name: Error message here"
 * Object format: { name: "Creative Name", message: "Error message" }
 */
const parseItem = (item) => {
  if (typeof item === 'string') {
    const colonIndex = item.indexOf(':');
    if (colonIndex > 0) {
      return {
        name: item.substring(0, colonIndex).trim(),
        message: item.substring(colonIndex + 1).trim(),
      };
    }
    return { name: item, message: '' };
  }
  return { name: item.name || 'Unknown', message: item.message || '' };
};

/**
 * LaunchSummaryPanel - Displays a summary of campaign launch results
 *
 * Shows statistics, skipped items warnings, and line item details
 * from programmatic campaign launches.
 *
 * @param {Object} launchSummary - The launch summary data from dspConfig.launchSummary[campaignId]
 * @param {string} campaignId - The campaign ID (for reference)
 */
export const LaunchSummaryPanel = ({ launchSummary, campaignId }) => {
  if (!launchSummary) return null;

  const hasSkippedItems =
    launchSummary.skippedCreatives > 0 ||
    launchSummary.creativeSummary?.skipped?.length > 0;

  const hasFailedItems =
    launchSummary.failedLineItems > 0 ||
    launchSummary.failedCreatives > 0;

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    try {
      return new Date(timestamp).toLocaleString();
    } catch {
      return timestamp;
    }
  };

  return (
    <Card
      title={
        <span>
          <RocketOutlined style={{ marginRight: 8 }} />
          Launch Summary
          {launchSummary.mode === 'UPDATE' && (
            <Tag color="blue" style={{ marginLeft: 8 }}>UPDATE</Tag>
          )}
        </span>
      }
      extra={
        <Text type="secondary">
          {formatTimestamp(launchSummary.timestamp)}
        </Text>
      }
      style={{ marginBottom: 16 }}
      size="small"
    >
      {/* Statistics Row */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Statistic
            title="Line Items"
            value={`${launchSummary.successfulLineItems || 0}/${launchSummary.totalLineItems || 0}`}
            prefix={
              launchSummary.failedLineItems === 0 ? (
                <CheckCircleOutlined style={{ color: '#52c41a' }} />
              ) : (
                <WarningOutlined style={{ color: '#faad14' }} />
              )
            }
            valueStyle={{ fontSize: 18 }}
          />
        </Col>
        <Col span={6}>
          <Statistic
            title="Creatives"
            value={`${launchSummary.successfulCreatives || 0}/${launchSummary.totalCreatives || 0}`}
            prefix={
              launchSummary.failedCreatives === 0 ? (
                <CheckCircleOutlined style={{ color: '#52c41a' }} />
              ) : (
                <WarningOutlined style={{ color: '#faad14' }} />
              )
            }
            valueStyle={{ fontSize: 18 }}
          />
        </Col>
        <Col span={6}>
          <Statistic
            title="Skipped"
            value={launchSummary.skippedCreatives || 0}
            valueStyle={{
              color: launchSummary.skippedCreatives > 0 ? '#faad14' : '#52c41a',
              fontSize: 18,
            }}
            prefix={
              launchSummary.skippedCreatives > 0 ? (
                <WarningOutlined />
              ) : (
                <CheckCircleOutlined />
              )
            }
          />
        </Col>
        <Col span={6}>
          <Statistic
            title="Success Rate"
            value={launchSummary.creativeSuccessRate || '100%'}
            valueStyle={{ fontSize: 18 }}
          />
        </Col>
      </Row>

      {/* Skipped Items Warning */}
      {hasSkippedItems && (
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          message={`${launchSummary.skippedCreatives || launchSummary.creativeSummary?.skipped?.length || 0} Items Skipped`}
          description={
            <div>
              <ul style={{ paddingLeft: 20, marginBottom: 8, marginTop: 8 }}>
                {(launchSummary.creativeSummary?.skipped || []).map((item, i) => {
                  const parsed = parseItem(item);
                  return (
                    <li key={i}>
                      <Text strong>{parsed.name}</Text>{parsed.message && `: ${parsed.message}`}
                    </li>
                  );
                })}
              </ul>
              <Text type="secondary">
                <InfoCircleOutlined style={{ marginRight: 4 }} />
                To use app inventory lists, recreate them with inventoryUsage: &quot;apps&quot;
              </Text>
            </div>
          }
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Failed Items Alert */}
      {hasFailedItems && (
        <Alert
          type="error"
          showIcon
          icon={<CloseCircleOutlined />}
          message={`${(launchSummary.failedLineItems || 0) + (launchSummary.failedCreatives || 0)} Items Failed`}
          description={
            <div>
              {launchSummary.lineItemSummary?.failed?.length > 0 && (
                <>
                  <Text strong>Failed Line Items:</Text>
                  <ul style={{ paddingLeft: 20, marginBottom: 8, marginTop: 4 }}>
                    {launchSummary.lineItemSummary.failed.map((item, i) => {
                      const parsed = parseItem(item);
                      return (
                        <li key={i}>
                          <Text strong>{parsed.name}</Text>{parsed.message && `: ${parsed.message}`}
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
              {launchSummary.creativeSummary?.failed?.length > 0 && (
                <>
                  <Text strong>Failed Creatives:</Text>
                  <ul style={{ paddingLeft: 20, marginBottom: 8, marginTop: 4 }}>
                    {launchSummary.creativeSummary.failed.map((item, i) => {
                      const parsed = parseItem(item);
                      return (
                        <li key={i}>
                          <Text strong>{parsed.name}</Text>{parsed.message && `: ${parsed.message}`}
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
            </div>
          }
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Targeting Warnings */}
      {launchSummary.targetingSummary?.totalWarnings > 0 && (
        <Alert
          type="info"
          showIcon
          icon={<InfoCircleOutlined />}
          message={`${launchSummary.targetingSummary.totalWarnings} Targeting Warnings`}
          description={
            <ul style={{ paddingLeft: 20, marginBottom: 0, marginTop: 8 }}>
              {launchSummary.targetingSummary.warnings?.map((warning, i) => (
                <li key={i}>{warning}</li>
              ))}
            </ul>
          }
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Line Item Details (Collapsible) */}
      {launchSummary.lineItemDetails?.length > 0 && (
        <Collapse ghost>
          <Collapse.Panel header="Line Item Details" key="details">
            {launchSummary.lineItemDetails.map((li, i) => (
              <div key={i} style={{ marginBottom: 8, paddingLeft: 8 }}>
                <Tag color={li.status === 'success' ? 'success' : 'error'}>
                  {li.status === 'success' ? (
                    <CheckCircleOutlined />
                  ) : (
                    <CloseCircleOutlined />
                  )}
                </Tag>
                <Text strong style={{ marginLeft: 4 }}>{li.lineItemName}</Text>
                <Text type="secondary" style={{ marginLeft: 8 }}>
                  ({li.creativeSummary?.successful?.length || 0} creatives
                  {li.creativeSummary?.skipped?.length > 0 && (
                    <span>, {li.creativeSummary.skipped.length} skipped</span>
                  )}
                  {li.creativeSummary?.failed?.length > 0 && (
                    <span>, {li.creativeSummary.failed.length} failed</span>
                  )}
                  )
                </Text>
                {li.targetingWarnings?.length > 0 && (
                  <Tag color="warning" style={{ marginLeft: 8 }}>
                    {li.targetingWarnings.length} warnings
                  </Tag>
                )}
                {li.errorMessage && (
                  <div style={{ marginLeft: 24, marginTop: 4 }}>
                    <Text type="danger">{li.errorMessage}</Text>
                  </div>
                )}
              </div>
            ))}
          </Collapse.Panel>
        </Collapse>
      )}
    </Card>
  );
};

export default LaunchSummaryPanel;
